import type { ReactNode } from "react";
import Link from "next/link";
import { themeClasses, type Theme } from "@cw/design-tokens/theme";
import { ContainedSection, SectionHeader } from "../primitives/Section";

export interface Pillar { title: string; subtitle?: string; description: string; href?: string; icon?: ReactNode }
export interface PillarGridProps { eyebrow?: string; heading?: string; intro?: ReactNode; items: Pillar[]; columns?: 2 | 3 | 4; theme?: Theme; id?: string }

const COLS = { 2: "md:grid-cols-2", 3: "md:grid-cols-2 lg:grid-cols-3", 4: "md:grid-cols-2 lg:grid-cols-4" };

export function PillarCard({ item, theme = "paper", headingTag: H = "h3", className = "" }: { item: Pillar; theme?: Theme; headingTag?: "h2" | "h3"; className?: string }) {
  const t = themeClasses(theme);
  const inner = (
    <>
      {item.icon ? <div className={`mb-5 ${t.accentText}`}>{item.icon}</div> : null}
      {item.subtitle ? <p className={`${t.muted} mb-2 font-sans text-xs font-semibold uppercase tracking-[0.16em]`}>{item.subtitle}</p> : null}
      <H className={`${t.heading} font-display text-xl font-semibold ${item.href ? t.accentTextGroupHover : ""} transition-colors`}>{item.title}</H>
      <p className={`${t.body} mt-3 text-[15px] leading-relaxed`}>{item.description}</p>
      {item.href ? <span className={`${t.accentText} mt-5 inline-flex items-center gap-1.5 text-sm font-semibold`}>Learn more <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">→</span></span> : null}
    </>
  );
  const cls = `group flex h-full flex-col rounded-card ${t.card} p-7 shadow-card transition-all duration-300 ease-out-soft ${item.href ? "hover:-translate-y-0.5 hover:shadow-card-hover" : ""} ${className}`;
  return item.href ? <Link href={item.href} className={cls}>{inner}</Link> : <div className={cls}>{inner}</div>;
}

export function PillarGrid({ eyebrow, heading, intro, items, columns = 3, theme = "paper", id }: PillarGridProps) {
  return (
    <ContainedSection theme={theme} id={id}>
      <SectionHeader eyebrow={eyebrow} heading={heading} intro={intro} theme={theme} />
      <ul className={`grid gap-5 ${COLS[columns]}`}>
        {items.map((it) => <li key={it.title}><PillarCard item={it} theme={theme} headingTag={heading ? "h3" : "h2"} /></li>)}
      </ul>
    </ContainedSection>
  );
}
